import {useMutation} from '@tanstack/react-query';
import {useState} from 'react';
import {useNotifications} from '../../../shared';
import {api} from '../../../shared';

interface UploadPrepareItem {
  id: string;
  url: string;
}

interface PrepareUploadResponse {
  items: UploadPrepareItem[];
}

interface UseDocumentUploadResult {
  uploadId: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileExtension: string | null;
  isUploading: boolean;
  uploadDocument: (file: File) => void;
  resetUpload: () => void;
}

/**
 * Утилита для прямой загрузки файла
 */
const uploadFileDirectly = async (file: File, uploadUrl: string): Promise<void> => {
  const buffer = await file.arrayBuffer();
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: buffer,
  });

  if (!response.ok) {
    let errorBody = 'Unknown error during direct upload.';
    try {
      errorBody = await response.text();
    } catch (e) {
      // Ignore error reading body
    }
    throw new Error(`Direct upload failed with status ${response.status}: ${errorBody}`);
  }
};

/**
 * Получаем расширение файла
 */
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Хук для загрузки документа проекта
 */
export const useProjectDocumentUpload = (): UseDocumentUploadResult => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [fileExtension, setFileExtension] = useState<string | null>(null);
  const {createNotification} = useNotifications();

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<string> => {
      // Получаем URL для загрузки
      const {data: response} = await api.post<PrepareUploadResponse>('/api/files/public/prepare-upload', {
        contentTypes: [file.type],
      });

      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      // Загружаем файл напрямую
      await uploadFileDirectly(file, response.items[0].url);

      return response.items[0].id;
    },
    onSuccess: (id, file) => {
      setUploadId(id);
      setFileName(file.name);
      setFileSize(file.size);
      setFileExtension(getFileExtension(file.name));
    },
    onError: (err: Error) => {
      createNotification({
        name: 'document-upload-error',
        title: 'Ошибка загрузки документа',
        content: err.message,
        theme: 'danger',
      });
      setUploadId(null);
      setFileName(null);
      setFileSize(null);
      setFileExtension(null);
    },
  });

  const resetUpload = () => {
    setUploadId(null);
    setFileName(null);
    setFileSize(null);
    setFileExtension(null);
    uploadMutation.reset();
  };

  return {
    uploadId,
    fileName,
    fileSize,
    fileExtension,
    isUploading: uploadMutation.isPending,
    uploadDocument: uploadMutation.mutate,
    resetUpload,
  };
};

